import React, { useEffect, useState } from 'react';

import { DashboardLayout, Button, Spinner } from "components";
import { chargeMethodList } from 'constants/lists';

import { 
    useAlertMessage,
    useBillingSetupDiscounts,
    useCompanyInputs, useProductInputs, 
    useChargeInputs, useMiscellaneousInputs,
    useSubmission
} from "./hooks";
import { func_validation, getObjectPropertyValue_stringTrimNotEmpty, getStringUniqueId, getWindowHeight } from "functions";

import "assets/scss/billingPortal/modules/recursiveBillingSetup/accountUpdate/accountUpdate.scss";
import { useOnPageExit } from 'hooks/useOnPageExit';
import { useSideInputDisplay } from './hooks/useSideInputDisplay';



export default function AccountUpdate(props) {    
    // Props  
    const { 
        isEditing, 
        isAccountRecordUpdateRequesting: isLoading,
        billingSetup,
        billingSetupId,
        billingSetupDiscountId
    } = props;

    let billingSetupDetails = func_validation.getObjectPropertyValue(billingSetup, "BillingSetupDetails");

    let referenceValue_original = "";
    if (func_validation.isArrayNotEmpty(billingSetupDetails)) {
        billingSetupDetails.map((detail, idx) => {
            let referenceValue = func_validation.getObjectPropertyValue(detail, "ReferenceValue");
            if (referenceValue_original == "") {
                referenceValue_original = referenceValue + "\n";
            }
            else {
                referenceValue_original += referenceValue + "\n";
            }
        });
    }
    let referenceValue_init = func_validation.isNotNullUndefined(referenceValue_original)? referenceValue_original: "";
    
    const [isFormSubmissionAttempted, setFormSubmissionStatus] = useState(false);
    const [referenceValue, setReferenceValue] = useState(referenceValue_init);
    let referenceValuePlaceholder_init = "";
    if (func_validation.isStringTrimNotEmpty(referenceValue)) {
        let totalReferenceValues = referenceValue.trim().split("\n").length;
        referenceValuePlaceholder_init = totalReferenceValues + " value(s)";
    }
    const [referenceValuePlaceholder, setReferenceValuePlaceholder] = useState(referenceValuePlaceholder_init);
    const [inputWrapperID] = useState(getStringUniqueId("input-wrapper"));
    const [headerID] = useState(getStringUniqueId("header"));

    let chargeMethod_init = getObjectPropertyValue_stringTrimNotEmpty(billingSetup, "ChargeMethod", "Outlet Transaction");
    chargeMethod_init = chargeMethodList.find((obj) => obj?.label === chargeMethod_init)?.value;
    const [chargeMethod, setChargeMethod] = useState(chargeMethod_init);


    // Page Header
    let pageHeaderTitle = isEditing? "Edit Account": "Add Account";

    useEffect(() => {
        let headerEle = document.getElementById(headerID);
        let inputWrapperEle = document.getElementById(inputWrapperID);

        let headerRect = headerEle?.getBoundingClientRect();
        inputWrapperEle.style.height = (getWindowHeight() - headerRect?.bottom - 25) + "px"; // 25px is because of padding bottom
    }, []);

    /* Hooks */

    // Alert Message
    const {
        alertMessageDisplay, setAlertMsg
    } = useAlertMessage({
        ...props,
        isFormSubmissionAttempted
    });

    // Side Display
    const {
        sideDetails,
        isDefaultProduct, setIsDefaultProduct,
        setIsDefaultProductDisabled,
        isDraft, // Get isDraft state from side inputs

        isEveryInputValid: isEveryInputValid_side,
        display: sideInputDisplay, 
        isAnyInputChanged: isAnyInputChanged_side
    } = useSideInputDisplay({
        ...props,
        referenceValue, setReferenceValue, isFormSubmissionAttempted,
        chargeMethod,
        setReferenceValuePlaceholder
    });

    // Company
    const { 
        companyId, companyDetails,
        isTypingAccountCode,
        isEveryInputValid: isEveryInputValid_company,
        display: companyInputDisplay,
        isAnyInputChanged: isAnyInputChanged_company
    } = useCompanyInputs({
        ...props, 
        isFormSubmissionAttempted,
        isDraft,
    });

    // Charge
    const { 
        chargeDetails,

        isEveryInputValid: isEveryInputValid_charge,
        isAnyInputChanged: isAnyInputChanged_charge,
        display: chargeInputDisplay
    } = useChargeInputs({
        ...props, 
        isFormSubmissionAttempted,
        referenceValue, setReferenceValue,
        setReferenceValuePlaceholder,
        isDefaultProduct, setIsDefaultProduct,
        chargeMethod, setChargeMethod, chargeMethod_init,
        setIsDefaultProductDisabled,
        isDraft,
    });
    
    // Product
    const { 
        productCode, productDetails,
        isEveryInputValid: isEveryInputValid_product,
        display: productInputDisplay,
        isAnyInputChanged: isAnyInputChanged_product
    } = useProductInputs({
        ...props, 
        isFormSubmissionAttempted,
        isDraft,
    });

    // Miscellaneous
    const { 
        miscellaneousDetails,

        isEveryInputValid: isEveryInputValid_miscellaneous,
        display: miscellaneousInputDisplay, 
        isAnyInputChanged: isAnyInputChanged_miscellaneous
    } = useMiscellaneousInputs({
        ...props, 
        isFormSubmissionAttempted, 
        isDefaultProduct,
        referenceValue, setReferenceValue,
        referenceValuePlaceholder, setReferenceValuePlaceholder, referenceValuePlaceholder_init,
        referenceValue_init,
        companyDetails, isTypingAccountCode,
        chargeDetails,
        isDraft,
    });

    // Billing Setup Discounts
    const {
        billingSetupDiscountsDetails,

        isEveryInputValid: isEveryInputValid_discounts,
        display: discountsInputDisplay,
        isAnyInputChanged: isAnyInputChanged_discounts
    } = useBillingSetupDiscounts({
        ...props,
        isFormSubmissionAttempted,
        isDraft,
        billingSetupId,
        billingSetupDiscountId
    });

    // Input Check
    let isEveryInputValid = (
        isEveryInputValid_company &&
        isEveryInputValid_product &&
        isEveryInputValid_charge &&
        isEveryInputValid_miscellaneous &&
        isEveryInputValid_discounts &&
        isEveryInputValid_side     
    )
    let isAnyInputChanged = isAnyInputChanged_company || isAnyInputChanged_product || 
        isAnyInputChanged_charge || isAnyInputChanged_miscellaneous || isAnyInputChanged_discounts ||isAnyInputChanged_side;

    // Submission
    const {
        submitIcon, submitText, submitAltText,
        handleSubmission
    } = useSubmission({
        ...props,

        isEveryInputValid, isAnyInputChanged,

        referenceValue, productCode, companyId,

        companyDetails, productDetails,

        billingSetupDiscountsDetails,

        chargeDetails, miscellaneousDetails,
        sideDetails,

        isFormSubmissionAttempted, setFormSubmissionStatus,
        setAlertMsg
    })
    

    // Handle Page Exit
    const {
        handleHideFormPage,
        pageExitModal
    } = useOnPageExit({
        ...props,
        handleSubmission,
        isAnyInputChanged,
        isEveryInputValid
    });

    return (
        <DashboardLayout
            headerTitle={pageHeaderTitle}
            
            onClose={isAnyInputChanged && handleHideFormPage}
            isHeaderCloseIconVisible

            bodyClassName={"recursiveBillingSetupAccountUpdate"}
            body={(
                <React.Fragment>
                    {/* Page Exit Alert */}
                    {pageExitModal}

                    <div id={headerID} className="recursiveBillingSetupAccountUpdate-subHeader d-flex justify-content-between">
                        {/* Title */}
                        <span className={"recursiveBillingSetupAccountUpdate-subHeader-title"}>{isEditing? "Update Info": "Insert Info"}</span>

                        {/* Action */}
                        <Button
                            className={"recursiveBillingSetupAccountUpdate-subHeader-action"}
                            handleOnClick={handleSubmission}
                            isDecoration_transparentDarkGreyBorder
                        >
                            <div className={"d-flex flex-column align-items-center justify-content-center"}>
                                {isLoading? (
                                    <Spinner size={0} isLeftMarginOptional />
                                ):(
                                    <img
                                        src={submitIcon}
                                        alt={submitAltText}
                                        className={"recursiveBillingSetupAccountUpdate-subHeaderAction-icon"}
                                    />
                                )}
                                <div className={"recursiveBillingSetupAccountUpdate-subHeaderAction-text"}>
                                    {submitText}
                                </div>
                            </div>
                        </Button>
                    </div>

                    <div id={inputWrapperID} className="recursiveBillingSetupAccountUpdate-inputWrapper">
                        {/* Alert Error Messages */}
                        {alertMessageDisplay}
                        
                        <div className={"d-flex h-100"}>
                            <div className={"input-wrapper__left-section"}>
                                {/* Company Info */}
                                {companyInputDisplay}
                    
                                {/* Product Info */}
                                {productInputDisplay}
                    
                                {/* Charge Info */}
                                {chargeInputDisplay}
                    
                                {/* Billing Setup Discounts */}
                                {discountsInputDisplay}

                                {/* Miscellaneous Info */}
                                {miscellaneousInputDisplay}
                            </div>

                            <div className={"input-wrapper__right-section"}>
                                {sideInputDisplay}
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            )}

            updateParentState_visiblity={props.updateParentState_visiblity}
        />
    );
}